import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Briefcase,
  Calendar,
  Camera,
  Check,
  CheckCircle2,
  Copy,
  Loader2,
  Mail,
  Paperclip,
  Phone,
  Send,
  ShieldCheck,
  User,
  UserPlus,
  X,
} from 'lucide-react';

import {
  chargerReferences,
  creerTechnicien,
  type DocumentJoint,
  type HabilitationChoisie,
  type References,
} from '../api/techniciens';

import { usePiegeFocus } from '../hooks/usePiegeFocus';

/** 2 Mo : la limite annoncée dans le formulaire, et celle que le serveur applique. */
const TAILLE_MAX = 2 * 1024 * 1024;

const CLASSE_CHAMP =
  'w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 outline-none transition-all text-sm';
const CLASSE_ICONE = 'w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400';

/** Lit un fichier en base64 — le format que l'API attend. */
function lireEnBase64(fichier: File): Promise<DocumentJoint> {
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader();
    lecteur.onerror = () => rejeter(new Error('Lecture du fichier impossible.'));
    lecteur.onload = () => {
      const resultat = String(lecteur.result ?? '');
      const virgule = resultat.indexOf(',');
      resoudre({
        nomFichier: fichier.name,
        typeMime: fichier.type,
        contenu: virgule >= 0 ? resultat.slice(virgule + 1) : resultat,
      });
    };
    lecteur.readAsDataURL(fichier);
  });
}

interface Proprietes {
  readonly ouvert: boolean;
  readonly onFermer: () => void;
  /** Appelé après création : l'écran parent rafraîchit sa liste. */
  readonly onCree?: () => void;
}

/**
 * Modale « Ajouter un technicien » : fiche complète ET invitation, en un geste.
 *
 * Le lien d'invitation n'est pas envoyé par courriel (port 25 filtré chez
 * l'hébergeur) : la modale le rend après création, une seule fois, pour que
 * l'administrateur le transmette lui-même. Le fermer sans le copier oblige à
 * révoquer et recommencer — c'est écrit à l'écran, pas seulement ici.
 */
export function ModaleTechnicien({ ouvert, onFermer, onCree }: Proprietes) {
  const [references, setReferences] = useState<References>();
  const [chargementRefs, setChargementRefs] = useState(true);
  const [nom, setNom] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [dateEmbauche, setDateEmbauche] = useState('');
  const [role, setRole] = useState('technicien');
  const [societe, setSociete] = useState('');
  const [actif, setActif] = useState(true);
  const [photo, setPhoto] = useState<DocumentJoint>();
  const [apercuPhoto, setApercuPhoto] = useState<string>();
  const [choisies, setChoisies] = useState<Record<string, HabilitationChoisie>>({});
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string>();
  const [lien, setLien] = useState<{ lien: string; expireLe: string; nom: string }>();
  const premierChamp = useRef<HTMLInputElement>(null);
  const boite = useRef<HTMLDivElement>(null);

  /**
   * Fenêtre modale : le focus reste DEDANS tant qu'elle est ouverte, et revient
   * au bouton qui l'a ouverte à la fermeture. Sans cela, la tabulation traverse
   * la page derrière — les deux autres modales du dépôt le font déjà.
   */
  usePiegeFocus(boite, ouvert);

  useEffect(() => {
    if (!ouvert) return;
    const controleur = new AbortController();
    setChargementRefs(true);
    void chargerReferences({ signal: controleur.signal })
      .then((resultat) => {
        if (resultat.ok && resultat.donnees) {
          // Listes possiblement vides ou partielles : l'écran ne doit pas casser
          // pour autant, il montre ce qu'il a et laisse le serveur refuser.
          const roles = resultat.donnees.roles ?? [];
          const habilitations = resultat.donnees.habilitations ?? [];
          setReferences({ roles, habilitations });
          setRole((actuel) =>
            roles.some((r) => r.code === actuel) ? actuel : (roles[0]?.code ?? 'technicien'),
          );
        } else {
          setErreur(resultat.message ?? 'Listes du formulaire indisponibles.');
        }
        setChargementRefs(false);
      })
      .catch(() => {
        /* abandon : modale fermée */
      });
    return () => controleur.abort();
  }, [ouvert]);

  useEffect(() => {
    if (ouvert) premierChamp.current?.focus();
  }, [ouvert]);

  const reinitialiser = () => {
    setNom('');
    setEmail('');
    setTelephone('');
    setDateEmbauche('');
    setSociete('');
    setActif(true);
    setPhoto(undefined);
    setApercuPhoto(undefined);
    setChoisies({});
    setErreur(undefined);
    setLien(undefined);
  };

  const fermer = () => {
    reinitialiser();
    onFermer();
  };

  const cocher = (code: string, coche: boolean, categories: ReadonlyArray<string> | null) => {
    setChoisies((actuelles) => {
      const suivantes = { ...actuelles };
      if (!coche) {
        delete suivantes[code];
      } else {
        suivantes[code] = {
          code,
          // Une habilitation simple n'a pas de catégorie ; un CACES en exige une,
          // que l'utilisateur choisit juste après (le select s'ouvre alors).
          categorie: categories ? '' : undefined,
        };
      }
      return suivantes;
    });
  };

  const changerCategorie = (code: string, categorie: string) => {
    setChoisies((actuelles) => ({
      ...actuelles,
      [code]: { ...actuelles[code], code, categorie },
    }));
  };

  const joindre = async (code: string, fichier: File) => {
    if (fichier.size > TAILLE_MAX) {
      setErreur(`« ${fichier.name} » pèse plus de 2 Mo : il ne sera pas joint.`);
      return;
    }
    setErreur(undefined);
    const document = await lireEnBase64(fichier);
    setChoisies((actuelles) => ({
      ...actuelles,
      [code]: { ...actuelles[code], code, justificatif: document },
    }));
  };

  const choisirPhoto = async (fichier: File) => {
    if (!['image/png', 'image/jpeg'].includes(fichier.type)) {
      setErreur('La photo doit être un JPG ou un PNG.');
      return;
    }
    if (fichier.size > TAILLE_MAX) {
      setErreur('La photo dépasse 2 Mo.');
      return;
    }
    setErreur(undefined);
    setPhoto(await lireEnBase64(fichier));
    setApercuPhoto(URL.createObjectURL(fichier));
  };

  const soumettre = async (evenement: React.FormEvent) => {
    evenement.preventDefault();
    setEnvoi(true);
    setErreur(undefined);

    const resultat = await creerTechnicien({
      nom,
      email,
      telephone: telephone.trim() || undefined,
      dateEmbauche: dateEmbauche || undefined,
      role,
      societe: societe.trim() || undefined,
      actif,
      photo,
      habilitations: Object.values(choisies),
    });

    setEnvoi(false);

    if (resultat.ok && resultat.donnees) {
      // Le lien n'apparaîtra plus : c'est le seul moment pour le copier.
      setLien({
        lien: `${globalThis.location.origin}${resultat.donnees.invitation.lien}`,
        expireLe: resultat.donnees.invitation.expireLe,
        nom: resultat.donnees.technicien.nom,
      });
      onCree?.();
    } else {
      setErreur(resultat.message ?? 'Création impossible.');
    }
  };

  const habilitations = references?.habilitations ?? [];
  const generales = useMemo(
    () => habilitations.filter((h) => h.categories === null),
    [habilitations],
  );
  const caces = useMemo(() => habilitations.filter((h) => h.categories !== null), [habilitations]);

  if (!ouvert) return null;

  return (
    <div
      ref={boite}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="titre-modale-technicien"
      onKeyDown={(evenement) => {
        // Échap ferme, comme dans les autres modales du dépôt.
        if (evenement.key === 'Escape') onFermer();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden border border-slate-200">
        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
          <h2
            id="titre-modale-technicien"
            className="text-lg font-semibold text-slate-800 flex items-center gap-2"
          >
            <span className="w-8 h-8 rounded-lg bg-red-100 text-red-600 flex items-center justify-center">
              <UserPlus className="w-4 h-4" aria-hidden="true" />
            </span>
            Ajouter un technicien
          </h2>
          <button
            type="button"
            onClick={fermer}
            aria-label="Fermer"
            className="text-slate-400 hover:text-red-500 hover:bg-red-50 p-1.5 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        {lien ? (
          <div className="p-6 space-y-4">
            <div className="flex items-start gap-3 rounded-lg bg-green-50 border border-green-200 p-4">
              <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-slate-800">
                  Fiche de {lien.nom} créée, invitation prête.
                </p>
                <p className="text-xs text-slate-600 mt-1">
                  Valable jusqu’au {lien.expireLe.slice(0, 16)}. Aucun courriel n’est envoyé :
                  transmettez ce lien vous-même. Il ne sera plus affiché, et il ne sert qu’une fois.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={lien.lien}
                aria-label="Lien d’invitation"
                onFocus={(evenement) => evenement.target.select()}
                className="flex-1 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700 mono"
              />
              <button
                type="button"
                onClick={() => void navigator.clipboard?.writeText(lien.lien)}
                className="flex items-center gap-2 px-3 py-2 border border-slate-300 text-slate-700 text-xs rounded-lg hover:bg-slate-50"
              >
                <Copy className="w-3.5 h-3.5" aria-hidden="true" />
                Copier
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={(evenement) => void soumettre(evenement)} className="contents">
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Informations personnelles */}
              <section>
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  Informations personnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2 flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400 overflow-hidden">
                      {apercuPhoto ? (
                        <img
                          src={apercuPhoto}
                          alt="Aperçu de la photo"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <Camera className="w-6 h-6" aria-hidden="true" />
                      )}
                    </div>
                    <div>
                      <label
                        htmlFor="photo-technicien"
                        className="text-sm text-red-600 font-medium hover:underline cursor-pointer"
                      >
                        Ajouter une photo
                      </label>
                      <input
                        id="photo-technicien"
                        type="file"
                        accept="image/png, image/jpeg"
                        className="sr-only"
                        onChange={(evenement) => {
                          const fichier = evenement.target.files?.[0];
                          if (fichier) void choisirPhoto(fichier);
                        }}
                      />
                      <p className="text-xs text-slate-500 mt-1">JPG, PNG. 2 Mo maximum.</p>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="nom" className="block text-sm font-medium text-slate-700 mb-1">
                      Nom complet <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <User className={CLASSE_ICONE} aria-hidden="true" />
                      <input
                        id="nom"
                        ref={premierChamp}
                        value={nom}
                        onChange={(evenement) => setNom(evenement.target.value)}
                        required
                        placeholder="Jean Dupont"
                        className={`${CLASSE_CHAMP} pl-10`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <Mail className={CLASSE_ICONE} aria-hidden="true" />
                      <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(evenement) => setEmail(evenement.target.value)}
                        required
                        placeholder="j.dupont@societe.fr"
                        className={`${CLASSE_CHAMP} pl-10`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="telephone"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Téléphone
                    </label>
                    <div className="relative">
                      <Phone className={CLASSE_ICONE} aria-hidden="true" />
                      <input
                        id="telephone"
                        type="tel"
                        value={telephone}
                        onChange={(evenement) => setTelephone(evenement.target.value)}
                        placeholder="06 12 34 56 78"
                        className={`${CLASSE_CHAMP} pl-10`}
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="date-embauche"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Date d’embauche
                    </label>
                    <div className="relative">
                      <Calendar className={CLASSE_ICONE} aria-hidden="true" />
                      <input
                        id="date-embauche"
                        type="date"
                        value={dateEmbauche}
                        onChange={(evenement) => setDateEmbauche(evenement.target.value)}
                        className={`${CLASSE_CHAMP} pl-10 text-slate-600`}
                      />
                    </div>
                  </div>
                </div>
              </section>

              {/* Informations professionnelles */}
              <section className="pt-4 border-t border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  Informations professionnelles
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="role" className="block text-sm font-medium text-slate-700 mb-1">
                      Rôle <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="role"
                      value={role}
                      onChange={(evenement) => setRole(evenement.target.value)}
                      required
                      className={`${CLASSE_CHAMP} bg-white`}
                    >
                      {(references?.roles ?? []).map((entree) => (
                        <option key={entree.code} value={entree.code}>
                          {entree.libelle}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="societe"
                      className="block text-sm font-medium text-slate-700 mb-1"
                    >
                      Société / Sous-traitant
                    </label>
                    <input
                      id="societe"
                      value={societe}
                      onChange={(evenement) => setSociete(evenement.target.value)}
                      placeholder="Nom de l’entreprise"
                      className={CLASSE_CHAMP}
                    />
                  </div>
                </div>
              </section>

              {/* Habilitations & certifications */}
              <section className="pt-4 border-t border-slate-100">
                <h3 className="text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  Habilitations &amp; Certifications
                </h3>

                {chargementRefs && <p className="text-sm text-slate-500">Chargement des listes…</p>}

                {!chargementRefs && habilitations.length === 0 && (
                  // Cas réellement rencontré : un serveur plus ancien que l'écran ne
                  // connaît pas la route des listes. Sans ce message, la section
                  // restait un grand vide et l'écran semblait cassé.
                  <p
                    role="alert"
                    className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                  >
                    Les habilitations n’ont pas pu être chargées. Vérifiez que le serveur qui tourne
                    est à jour (binaire de la même version que l’écran), puis rechargez.
                  </p>
                )}

                {/* Habilitations de base */}
                <div className="mb-6">
                  <span className="block text-sm font-medium text-slate-700 mb-2">
                    Habilitations générales
                  </span>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {generales.map((habilitation) => (
                      <div
                        key={habilitation.code}
                        className="flex items-center justify-between p-3 rounded-lg border border-slate-300 hover:bg-slate-50 transition-colors"
                      >
                        <CaseACocher
                          libelle={habilitation.libelle}
                          cochee={habilitation.code in choisies}
                          onCocher={(coche) => cocher(habilitation.code, coche, null)}
                        />
                        <Trombone
                          etiquette={habilitation.libelle}
                          joint={choisies[habilitation.code]?.justificatif}
                          onJoindre={(fichier) => void joindre(habilitation.code, fichier)}
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* CACES */}
                <div className="pt-4 border-t border-slate-100">
                  <span className="block text-sm font-medium text-slate-700 mb-3">
                    CACES (Conduite d’engins)
                  </span>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {caces.map((habilitation) => {
                      const choisie = choisies[habilitation.code];
                      return (
                        <div
                          key={habilitation.code}
                          className="bg-slate-50 border border-slate-200 rounded-xl p-4 transition-colors hover:border-slate-300 flex flex-col gap-2"
                        >
                          <CaseACocher
                            libelle={habilitation.libelle}
                            cochee={Boolean(choisie)}
                            gras
                            onCocher={(coche) =>
                              cocher(habilitation.code, coche, habilitation.categories)
                            }
                          />
                          <select
                            aria-label={`Catégorie du ${habilitation.libelle}`}
                            disabled={!choisie}
                            value={choisie?.categorie ?? ''}
                            onChange={(evenement) =>
                              changerCategorie(habilitation.code, evenement.target.value)
                            }
                            className={`w-full px-3 py-1.5 border border-slate-300 rounded-lg text-xs outline-none focus:ring-2 focus:ring-red-500 ${
                              choisie
                                ? 'bg-white text-slate-700'
                                : 'bg-slate-100 text-slate-500 cursor-not-allowed opacity-50'
                            }`}
                          >
                            <option value="">Catégorie…</option>
                            {(habilitation.categories ?? []).map((categorie) => (
                              <option key={categorie} value={categorie}>
                                Catégorie {categorie}
                              </option>
                            ))}
                          </select>
                          <div className="mt-1 flex items-center justify-between border-t border-slate-200 pt-2">
                            <span className="text-[10px] text-slate-500">Justificatif</span>
                            <Trombone
                              etiquette={habilitation.libelle}
                              joint={choisie?.justificatif}
                              avecTexte
                              onJoindre={(fichier) => void joindre(habilitation.code, fichier)}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>

              {/* Statut */}
              <section className="pt-4 border-t border-slate-100">
                <div className="flex items-center justify-between bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-3">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={actif}
                        onChange={(evenement) => setActif(evenement.target.checked)}
                        className="sr-only peer"
                      />
                      <span className="w-11 h-6 bg-slate-300 peer-checked:bg-red-600 rounded-full relative transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full">
                        <span className="sr-only">Technicien actif</span>
                      </span>
                    </label>
                    <div>
                      <span className="text-sm font-medium text-slate-800">Technicien actif</span>
                      <p className="text-xs text-slate-500">
                        Le technicien pourra recevoir des interventions.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {erreur && (
                <p
                  role="alert"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
                >
                  {erreur}
                </p>
              )}
            </div>

            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={fermer}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={envoi || chargementRefs}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors shadow-sm flex items-center gap-2 disabled:opacity-60"
              >
                {envoi ? (
                  <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                ) : (
                  <Send className="w-4 h-4" aria-hidden="true" />
                )}
                {envoi ? 'Envoi en cours…' : 'Créer et envoyer l’invitation'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * La case du modèle : un carré blanc qui devient rouge avec une coche.
 *
 * L'`input` reste dans le document (classe `peer`, caché au lecteur d'écran) :
 * c'est lui qui porte l'état et le nom accessible, le carré n'est que sa
 * traduction visuelle. Un `div` cliquable aurait perdu le clavier et les tests.
 */
function CaseACocher({
  libelle,
  cochee,
  gras,
  onCocher,
}: {
  readonly libelle: string;
  readonly cochee: boolean;
  readonly gras?: boolean;
  readonly onCocher: (coche: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer flex-1">
      <input
        type="checkbox"
        checked={cochee}
        onChange={(evenement) => onCocher(evenement.target.checked)}
        className="peer sr-only"
      />
      <span className="w-5 h-5 rounded border border-slate-300 flex items-center justify-center peer-checked:bg-red-600 peer-checked:border-red-600 transition-colors shrink-0">
        {cochee && <Check className="w-3.5 h-3.5 text-white" aria-hidden="true" />}
      </span>
      <span
        className={`${gras ? 'font-semibold' : 'font-medium'} text-sm ${
          cochee ? 'text-red-700' : 'text-slate-700'
        }`}
      >
        {libelle}
      </span>
    </label>
  );
}

/** Le trombone : une fois le document joint, il devient une coche verte bavarde. */
function Trombone({
  etiquette,
  joint,
  avecTexte,
  onJoindre,
}: {
  readonly etiquette: string;
  readonly joint?: DocumentJoint;
  readonly avecTexte?: boolean;
  readonly onJoindre: (fichier: File) => void;
}) {
  const identifiant = `doc-${etiquette.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <>
      <label
        htmlFor={identifiant}
        title={joint ? `Document joint : ${joint.nomFichier}` : 'Joindre un justificatif'}
        className={`flex items-center gap-1 transition-colors cursor-pointer p-1 ${
          joint ? 'text-emerald-600' : 'text-slate-400 hover:text-blue-600'
        }`}
      >
        {joint ? (
          <CheckCircle2 className={avecTexte ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
        ) : (
          <Paperclip className={avecTexte ? 'w-3.5 h-3.5' : 'w-4 h-4'} aria-hidden="true" />
        )}
        {(avecTexte || joint) && (
          <span className={`text-xs ${joint ? 'text-emerald-600 font-medium' : 'text-slate-500'}`}>
            {joint ? 'Ajouté' : 'Joindre'}
          </span>
        )}
      </label>
      <input
        id={identifiant}
        type="file"
        accept="image/png, image/jpeg, application/pdf"
        className="sr-only"
        onChange={(evenement) => {
          const fichier = evenement.target.files?.[0];
          if (fichier) onJoindre(fichier);
        }}
      />
    </>
  );
}
